import React, { useEffect, useState } from "react";
import Plot from "react-plotly.js";
import { carColors } from "../../utils/carColors";

const API_URL = "https://iracing6-backend.herokuapp.com";

function LapTimesBoxPlotWidget({ config }) {
        const [data, setData] = useState([]);
        const [loading, setLoading] = useState(true);
        const [error, setError] = useState(null);
        const [weekNumber, setWeekNumber] = useState(null);
        const [weekNumberSelected, setWeekNumberSelected] = useState(null);
        const [isQualifying, setIsQualifying] = useState(true);
        const [trackNames, setTrackNames] = useState([]);

        const season = config.seasonName || "GT Sprint Series by Simucube - 2026 Season 4";

        // Fetch week number on mount
        useEffect(() => {
                async function fetchWeekNumber() {
                        try {
                                const response = await fetch(`${API_URL}/api/week-number`);
                                const json = await response.json();
                                const numericWeek = parseInt(json, 10);
                                setWeekNumber(numericWeek);
                                setWeekNumberSelected(numericWeek);
                        } catch (err) {
                                setError("Failed to fetch week number");
                        }
                }
                fetchWeekNumber();
        }, []);

        // Fetch track names for season
        useEffect(() => {
                async function fetchTrackNames() {
                        try {
                                const response = await fetch(`${API_URL}/api/series-basic-info/series-basic-info/seasonTracks/${encodeURIComponent(season)}`);
                                const json = await response.json();
                                setTrackNames(json);
                        } catch (err) {
                                console.error("Error fetching track names:", err);
                        }
                }
                fetchTrackNames();
        }, [season]);

        // Fetch lap time data
        useEffect(() => {
                if (weekNumberSelected === null || weekNumber === null) return;

                async function fetchData() {
                        try {
                                setLoading(true);
                                setError(null);

                                let endpoint;
                                if (weekNumberSelected === weekNumber) {
                                        endpoint = isQualifying
                                                ? `${API_URL}/api/series-avg-lap-times/average-lap-time/${encodeURIComponent(season)}`
                                                : `${API_URL}/api/series-avg-lap-times/average-race-lap-time/${encodeURIComponent(season)}`;
                                } else {
                                        endpoint = isQualifying
                                                ? `${API_URL}/api/series-avg-lap-times/average-lap-time-historic/${encodeURIComponent(season)}/${weekNumberSelected}`
                                                : `${API_URL}/api/series-avg-lap-times/average-race-lap-time-historic/${encodeURIComponent(season)}/${weekNumberSelected}`;
                                }

                                const response = await fetch(endpoint);

                                if (!response.ok && isQualifying) {
                                        setIsQualifying(false);
                                        return;
                                }

                                const json = await response.json();

                                if ((!Array.isArray(json) || json.length === 0) && isQualifying) {
                                        setIsQualifying(false);
                                        return;
                                }

                                if (!Array.isArray(json) || json.length === 0) {
                                        setData([]);
                                        setLoading(false);
                                        return;
                                }

                                // Filter outliers using IQR
                                const lapTimes = json.map((item) => item.best_lap_time_avg / 10000);
                                const sorted = [...lapTimes].sort((a, b) => a - b);
                                const Q1 = sorted[Math.floor(sorted.length / 4)];
                                const Q3 = sorted[Math.floor((3 * sorted.length) / 4)];
                                const IQR = Q3 - Q1;
                                const lowerBound = Q1 - 2 * IQR;
                                const upperBound = Q3 + 2 * IQR;

                                const filteredData = json.filter((item) => {
                                        const lapTime = item.best_lap_time_avg / 10000;
                                        return lapTime >= lowerBound && lapTime <= upperBound;
                                });

                                const sortedData = filteredData.sort((a, b) => a.i_rating_range[0] - b.i_rating_range[0]);
                                setData(sortedData);
                                setLoading(false);
                        } catch (err) {
                                setError(err.message);
                                setLoading(false);
                        }
                }

                fetchData();
        }, [season, isQualifying, weekNumberSelected, weekNumber]);

        if (loading || weekNumberSelected === null) {
                return (
                        <div className="widget-loading">
                                <div className="spinner-small"></div>
                        </div>
                );
        }

        if (error) {
                return <div className="widget-error">Error: {error}</div>;
        }

        if (!data || data.length === 0) {
                return <div className="widget-error">No lap time data available</div>;
        }

        // Group data by car class
        const groupedData = data.reduce((acc, item) => {
                const className = item.compositeKey.carClassName;
                if (!acc[className]) acc[className] = [];
                acc[className].push(item);
                return acc;
        }, {});

        const formatTime = (seconds) => {
                const minutes = Math.floor(seconds / 60);
                const remainingSeconds = (seconds % 60).toFixed(1);
                return `${minutes}:${remainingSeconds.padStart(4, "0")}`;
        };

        // Build line chart data (lap time vs iRating per class)
        const allXValues = data.flatMap((item) => item.i_rating_range);
        const minX = Math.min(...allXValues);
        const maxX = Math.max(...allXValues);
        const xValues = [];
        for (let x = minX; x <= maxX; x += 100) {
                xValues.push(x);
        }

        const classNames = Object.keys(groupedData);
        const isSingleClass = classNames.length === 1;

        const plotLines = classNames.map((className) => {
                const classData = groupedData[className];
                const yValues = xValues.map((x) => {
                        const item = classData.find((d) => x >= d.i_rating_range[0] && x <= d.i_rating_range[1]);
                        return item ? item.best_lap_time_avg / 10000 : null;
                });

                const firstNonNull = yValues.findIndex((v) => v !== null);
                const lastNonNull = yValues.length - 1 - [...yValues].reverse().findIndex((v) => v !== null);
                const trimmedX = xValues.slice(firstNonNull, lastNonNull + 1);
                const trimmedY = yValues.slice(firstNonNull, lastNonNull + 1);

                return {
                        x: trimmedX,
                        y: trimmedY,
                        type: "scatter",
                        mode: "lines",
                        marker: { color: isSingleClass ? "#f1c40f" : carColors[className] || "#f1c40f" },
                        name: className,
                        connectgaps: true,
                };
        });

        // Y-axis tick formatting
        const allY = data.map((item) => item.best_lap_time_avg / 10000);
        const yMax = Math.ceil(Math.max(...allY));
        const yMin = Math.floor(Math.min(...allY));
        const yRange = yMax - yMin + 1;
        const yStep = yRange > 30 ? 2 : 1;
        const yTickVals = [];
        for (let v = yMin; v <= yMax; v += yStep) {
                yTickVals.push(v);
        }
        const yTickText = yTickVals.map((s) => {
                const m = Math.floor(s / 60);
                const sec = Math.floor(s % 60);
                return `${m}:${sec.toString().padStart(2, "0")}`;
        });

        const currentTrack = trackNames[weekNumberSelected - 1] || "";

        const hasLegend = classNames.length > 1;

        const lineLayout = {
                margin: { l: 45, r: 10, t: 25, b: 45 },
                plot_bgcolor: "transparent",
                paper_bgcolor: "transparent",
                title: {
                        text: currentTrack.length > 35 ? currentTrack.substring(0, 35) + "..." : currentTrack,
                        font: { size: 10, color: "#aaa" },
                        x: 0.5,
                        y: 0.98,
                },
                xaxis: {
                        title: { text: "iRating", font: { size: 11, color: "#aaa" } },
                        color: "#aaa",
                        gridcolor: "#333",
                        fixedrange: true,
                        domain: [0, 1],
                        tick0: 0,
                        dtick: 500,
                        tickfont: { size: 10 },
                },
                yaxis: {
                        title: { text: isQualifying ? "Quali" : "Race", font: { size: 11, color: "#aaa" } },
                        color: "#aaa",
                        gridcolor: "#333",
                        fixedrange: true,
                        tickvals: yTickVals,
                        ticktext: yTickText,
                        tickfont: { size: 10 },
                },
                font: {
                        family: "Questrial, sans-serif",
                        size: 11,
                        color: "#ddd",
                },
                showlegend: false,
                hovermode: false,
                autosize: true,
        };

        return (
                <div className="widget-chart">
                        <div className="widget-lap-controls">
                                <button className={isQualifying ? "widget-lap-btn-active" : "widget-lap-btn"} onClick={() => setIsQualifying(true)}>
                                        Quali
                                </button>
                                <button className={!isQualifying ? "widget-lap-btn-active" : "widget-lap-btn"} onClick={() => setIsQualifying(false)}>
                                        Race
                                </button>
                                <select className="widget-lap-week-select" value={weekNumberSelected || ""} onChange={(e) => setWeekNumberSelected(parseInt(e.target.value))}>
                                        {Array.from({ length: weekNumber }, (_, i) => (
                                                <option key={i + 1} value={i + 1}>
                                                        Wk {i + 1}
                                                </option>
                                        ))}
                                </select>
                        </div>
                        <div style={{ position: "relative", width: "100%", height: "calc(100% - 30px)" }}>
                                <Plot data={plotLines} layout={lineLayout} config={{ displayModeBar: false, responsive: true }} style={{ width: "100%", height: "100%" }} useResizeHandler={true} />
                                {hasLegend && (
                                        <div
                                                style={{
                                                        position: "absolute",
                                                        top: 25,
                                                        right: 12,
                                                        background: "rgba(30,30,30,0.7)",
                                                        borderRadius: 3,
                                                        padding: "3px 5px",
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        gap: 0,
                                                        pointerEvents: "none",
                                                }}
                                        >
                                                {classNames.map((name) => (
                                                        <div key={name} style={{ display: "flex", alignItems: "center", gap: 4, lineHeight: 1 }}>
                                                                <span
                                                                        style={{
                                                                                width: 10,
                                                                                height: 3,
                                                                                backgroundColor: carColors[name] || "#f1c40f",
                                                                                display: "inline-block",
                                                                                flexShrink: 0,
                                                                        }}
                                                                />
                                                                <span className="lt-legend-label" style={{ fontSize: 9, color: "#ddd", whiteSpace: "nowrap" }}>
                                                                        {name}
                                                                </span>
                                                        </div>
                                                ))}
                                        </div>
                                )}
                        </div>
                </div>
        );
}

export default LapTimesBoxPlotWidget;
